import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';

import type { Session } from '@innovamat/ga-features';
import { useResource } from '@innovamat/glow-api-client';
import type { Region } from '@innovamat/localization';
import type { PrintingToolConfigurationType } from '@innovamat/smart-guides';
import {
  EarlyYearPrintable,
  SmartGuidePrintable,
} from '@innovamat/smart-guides';

import { useRegionParameters, useUpdateI18nLanguage } from '../../hooks';
import useGetPreprResource from '../../hooks/use-get-prepr-resource';
import { prepareSmartGuideForPrinting } from '../../utils/print-guides';

const BreakPage = styled.div`
  page-break-after: always;
`;

type Props = {
  id: string;
  hasBreakPage: boolean;
  isEditMode: boolean;
  language?: string | null;
  region?: string | null;
  courseOrder?: number | null;
  standard?: string | null;
  configuration?: PrintingToolConfigurationType;
};

function PrintGuide({
  id,
  hasBreakPage,
  isEditMode,
  language,
  region,
  courseOrder,
  standard,
  configuration,
}: Props): JSX.Element | null {
  const { t, i18n } = useTranslation();

  const languageToUse = language || 'es';
  const regionToUse = region || undefined;

  useUpdateI18nLanguage({ language: languageToUse });

  const { resource } = useResource({
    id: id,
    type: 'session',
    classroomId: undefined,
    language: language || 'es',
    academicYearId: undefined,
    isStudent: false,
    isLoggedIn: true,
    standard: standard ?? undefined,
    courseOrder: courseOrder ?? undefined,
    region: region ?? undefined,
  });

  const session = resource as Session;

  const { data: preprResource } = useGetPreprResource({
    id: session?.guide?.id!,
    session,
    locale: languageToUse,
    region: regionToUse as Region,
    enabled: !!session,
    isPrintable: true,
  });

  const { regionParameters } = useRegionParameters({
    defaultRegion: regionToUse,
    defaultCourseOrder: courseOrder ?? undefined,
  });

  const i18nTranslations =
    i18n.getDataByLanguage(i18n.language) || i18n.getDataByLanguage('es');

  const { translations } = i18nTranslations as any;

  if (preprResource?.type === 'smartGuide') {
    const finalPreprResource =
      configuration && preprResource
        ? prepareSmartGuideForPrinting(preprResource, configuration)
        : preprResource;

    return (
      <>
        <SmartGuidePrintable
          playerInfo={
            {
              appletInfo: {
                regionParameters,
              },
            } as any
          }
          isEditMode={isEditMode}
          smartGuide={finalPreprResource}
          session={session}
          translations={translations}
          t={t}
          setEventData={() => {}}
          region={regionToUse}
          hasBreakPage={hasBreakPage}
          standard={standard ?? undefined}
        />
        <BreakPage />
      </>
    );
  }

  if (
    preprResource?.type === 'digitalGuide' &&
    preprResource?.preprType === 'earlyYear'
  ) {
    return (
      <>
        <EarlyYearPrintable
          guide={preprResource}
          resource={{ ...session, language: languageToUse }}
          t={t}
          onEvent={() => {}}
          translations={translations}
        />
        <BreakPage />
      </>
    );
  }

  return null;
}

export { PrintGuide };
